import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { posthog, isPostHogConfigured } from '../lib/posthog';

// Generate a unique anonymous ID for this device
const generateAnonymousId = (): string => {
  return `anon_${crypto.randomUUID()}`;
};

// Migrate anonymous links to authenticated user
const migrateAnonymousLinks = async (accessToken: string, anonymousId: string): Promise<void> => {
  if (!anonymousId) return;

  try {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:54321/functions/v1';
    const response = await fetch(`${apiUrl}/migrate-links`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'X-Anonymous-Id': anonymousId,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      await response.json();
    }
  } catch (error) {
    // Silent fail - migration is best effort
  }
};

interface AuthState {
  // State
  user: User | null;
  session: Session | null;
  anonymousId: string;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Computed
  isAuthenticated: boolean;
  email: string | null;
  fullName: string | null;
  avatarUrl: string | null;

  // Actions
  initialize: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      session: null,
      anonymousId: '',
      isLoading: true,
      isInitialized: false,
      error: null,

      // Computed (updated via actions)
      isAuthenticated: false,
      email: null,
      fullName: null,
      avatarUrl: null,

      // Initialize auth - call this on app startup
      initialize: async () => {
        
        // Ensure we have an anonymous ID
        let { anonymousId } = get();
        if (!anonymousId) {
          anonymousId = generateAnonymousId();
          set({ anonymousId });
        }

        // If Supabase is not configured, just mark as initialized
        if (!isSupabaseConfigured() || !supabase) {
          set({ isLoading: false, isInitialized: true });
          
          // Track anonymous initialization
          if (isPostHogConfigured()) {
            posthog.capture('auth_initialized', {
              mode: 'anonymous',
              anonymousId,
            });
          }
          return;
        }

        try {
          // Get current session
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) {
            console.error('[Auth] Error getting session:', error);
            set({ isLoading: false, isInitialized: true, error: error.message });
            
            // Track initialization error
            if (isPostHogConfigured()) {
              posthog.capture('auth_error', {
                error: error.message,
                context: 'initialization',
              });
            }
            return;
          }

          if (session) {
          } else {
          }

          set({
            user: session?.user ?? null,
            session,
            isAuthenticated: Boolean(session?.user),
            email: session?.user?.email ?? null,
            fullName: session?.user?.user_metadata?.full_name ?? null,
            avatarUrl: session?.user?.user_metadata?.avatar_url ?? null,
            isLoading: false,
            isInitialized: true,
          });

          // Track successful initialization
          if (isPostHogConfigured()) {
            posthog.capture('auth_initialized', {
              authenticated: Boolean(session?.user),
              hasSession: Boolean(session),
            });
            
            // Identify user if authenticated
            if (session?.user) {
              posthog.identify(session.user.id, {
                email: session.user.email,
                name: session.user.user_metadata?.full_name,
              });
            }
          }

          // If user is already authenticated on init, migrate anonymous links
          if (session?.user && session.access_token) {
            await migrateAnonymousLinks(session.access_token, anonymousId);
          }

          // Listen for auth state changes with comprehensive event handling
          supabase.auth.onAuthStateChange(async (event, session) => {

            const wasAuthenticated = get().isAuthenticated;
            const nowAuthenticated = Boolean(session?.user);

            // Handle different auth events
            switch (event) {
              case 'SIGNED_IN':
                if (isPostHogConfigured()) {
                  posthog.capture('auth_sign_in_success', {
                    email: session?.user?.email,
                    provider: session?.user?.app_metadata?.provider,
                  });
                  // Identify the user
                  if (session?.user) {
                    posthog.identify(session.user.id, {
                      email: session.user.email,
                      name: session.user.user_metadata?.full_name,
                    });
                  }
                }
                break;
              case 'SIGNED_OUT':
                if (isPostHogConfigured()) {
                  posthog.capture('auth_sign_out');
                  posthog.reset();
                }
                break;
              case 'TOKEN_REFRESHED':
                if (isPostHogConfigured()) {
                  posthog.capture('auth_token_refreshed');
                }
                break;
              case 'USER_UPDATED':
                if (isPostHogConfigured()) {
                  posthog.capture('auth_user_updated');
                }
                break;
              case 'PASSWORD_RECOVERY':
                if (isPostHogConfigured()) {
                  posthog.capture('auth_password_recovery');
                }
                break;
            }

            set({
              user: session?.user ?? null,
              session,
              isAuthenticated: nowAuthenticated,
              email: session?.user?.email ?? null,
              fullName: session?.user?.user_metadata?.full_name ?? null,
              avatarUrl: session?.user?.user_metadata?.avatar_url ?? null,
            });

            // When user signs in (not just token refresh), migrate anonymous links
            if (!wasAuthenticated && nowAuthenticated && session?.user && event === 'SIGNED_IN') {
              await migrateAnonymousLinks(session.access_token, get().anonymousId);
            }
          });
          
        } catch (err) {
          console.error('[Auth] Error initializing auth:', err);
          set({ isLoading: false, isInitialized: true });
          
          // Track unexpected error
          if (isPostHogConfigured()) {
            posthog.capture('auth_error', {
              error: err instanceof Error ? err.message : 'Unknown error',
              context: 'initialization_exception',
            });
          }
        }
      },

      // Sign in with magic link
      signInWithEmail: async (email: string) => {
        if (!isSupabaseConfigured() || !supabase) {
          return { error: 'Auth not configured' };
        }

        set({ isLoading: true, error: null });
        
        // Track sign in attempt
        if (isPostHogConfigured()) {
          posthog.capture('auth_sign_in_email_start', {
            email,
          });
        }

        try {
          const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
              // Redirect back to the app after clicking the link
              emailRedirectTo: window.location.origin,
            },
          });

          if (error) {
            set({ isLoading: false, error: error.message });
            
            // Track sign in failure
            if (isPostHogConfigured()) {
              posthog.capture('auth_sign_in_email_failed', {
                email,
                error: error.message,
              });
            }
            
            return { error: error.message };
          }

          set({ isLoading: false });
          
          // Track magic link sent (success will be tracked on SIGNED_IN event)
          if (isPostHogConfigured()) {
            posthog.capture('auth_sign_in_email_sent', {
              email,
            });
          }
          
          return { error: null };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          set({ isLoading: false, error: message });
          
          // Track exception
          if (isPostHogConfigured()) {
            posthog.capture('auth_sign_in_email_failed', {
              email,
              error: message,
            });
          }
          
          return { error: message };
        }
      },

      // Sign in with Google OAuth
      signInWithGoogle: async () => {
        if (!isSupabaseConfigured() || !supabase) {
          return { error: 'Authentication not configured' };
        }

        set({ isLoading: true, error: null });
        
        // Track Google sign in attempt
        if (isPostHogConfigured()) {
          posthog.capture('auth_sign_in_google_start');
        }

        try {
          const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
              redirectTo: window.location.origin,
              queryParams: {
                access_type: 'offline',
                prompt: 'consent',
              },
              // Skip browser redirect for better control
              skipBrowserRedirect: false,
            },
          });

          if (error) {
            console.error('[Auth] Google OAuth error:', error);
            set({ isLoading: false, error: error.message });
            
            // Track Google OAuth failure
            if (isPostHogConfigured()) {
              posthog.capture('auth_sign_in_google_failed', {
                error: error.message,
              });
            }
            
            return { error: error.message };
          }

          // OAuth redirect happens here, loading state persists
          // Success will be tracked on SIGNED_IN event
          return { error: null };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          console.error('[Auth] Exception during Google sign in:', err);
          set({ isLoading: false, error: message });
          
          // Track exception
          if (isPostHogConfigured()) {
            posthog.capture('auth_sign_in_google_failed', {
              error: message,
            });
          }
          
          return { error: message };
        }
      },

      // Sign out
      signOut: async () => {
        if (!isSupabaseConfigured() || !supabase) {
          return;
        }

        set({ isLoading: true });

        try {
          await supabase.auth.signOut();
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            email: null,
            fullName: null,
            avatarUrl: null,
            isLoading: false,
          });
          
          // Track sign out (also tracked in onAuthStateChange SIGNED_OUT)
          // This ensures tracking even if listener fails
          if (isPostHogConfigured()) {
            posthog.capture('auth_sign_out');
            posthog.reset();
          }
        } catch (err) {
          console.error('Error signing out:', err);
          set({ isLoading: false });
          
          // Track sign out error
          if (isPostHogConfigured()) {
            posthog.capture('auth_error', {
              error: err instanceof Error ? err.message : 'Unknown error',
              context: 'sign_out',
            });
          }
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'refind-auth',
      // Only persist the anonymousId - Supabase handles session persistence
      partialize: (state) => ({ anonymousId: state.anonymousId }),
    }
  )
);

